import { BusinessHours } from '../models/business-hours.model';
import { Injectable, inject } from '@angular/core';
import { map, retry, catchError, switchMap } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { ShopSetup } from '../models/shop-setup.model';
import { environment } from 'src/environments/environment';
import { ShopInfo } from '../models/shop-info.model';

interface ShopDay {
  id: number;
  giorno: Date;
  orari: Date[];
}

@Injectable({
  providedIn: 'root',
})
export class ShopService {
  private http = inject(HttpClient);

  public currentShop = new BehaviorSubject<any>(new ShopInfo());

  getAllShops(): Observable<any[]> {
    return this.http.get(`${environment.API_URL}/shops`).pipe(
      map((data: any[]) => data),
      retry(3),
      catchError((error: HttpErrorResponse) => {
        if (error.error instanceof Error) {
          return throwError('Errore generico');
        } else {
          return throwError('Errore durante la connessione al server');
        }
      })
    );
  }

  getShop(idNegozio: string): Observable<ShopInfo> {
    return this.http
      .get(`${environment.API_URL}/shops/${idNegozio}`)
      .pipe(map((data: ShopInfo) => data));
  }

  /* Richiesta HTTP .GET all'API SETUP, la quale restituisce array di oggetti NegozioSetup, il seguente array contiene dati come
    raggio, giorni massimi, spese di spedizione e spedizione gratuita dopo x limite */
  getSetup(): Observable<ShopSetup> {
    return this.http
      .get(`${environment.API_URL}/shops/setup`)
      .pipe(map((data: any) => data));
  }

  /* Richiesta HTTP .GET all'API GIORNI, la quale restituisce array di oggetti NegozioGiorni contenenti i giorni disponibili
    per la consegna a domicilio o takeaway (la tipologia dei giorni è definita dalla variabile
    'tipo' e può contenere solo due stati: TAKEAWAY o ASPORTO) */
  getHours(type: string): Observable<BusinessHours[]> {
    return this.http
      .get(`${environment.API_URL}/shops/hours/`, {
        params: { type: type },
      })
      .pipe(map((data: BusinessHours[]) => data));
  }

  getShoppingDays(type: string): Observable<ShopDay[]> {
    return this.getSetup().pipe(
      switchMap((setup) =>
        this.getHours(type).pipe(
          map((days) => this.buildDays(type, days, setup))
        )
      )
    );
  }

  buildHours(orari: string[], giorno: Date): Date[] {
    return orari
      .map((o) => {
        const hour = o.split(':')[0];
        const min = o.split(':')[1];
        const date = new Date(giorno);
        if (Number(hour) >= 24) {
          date.setDate(date.getDate() + 1);
          date.setHours(Number(hour) - 24, Number(min), 0, 0);
        } else {
          date.setHours(Number(hour), Number(min), 0, 0);
        }
        return date;
      })
      .sort((a, b) => a.valueOf() - b.valueOf());
  }

  buildDays(
    type: string,
    businessHours: BusinessHours[],
    setup: ShopSetup
  ): ShopDay[] {
    const indices: { [key: string]: number } = {
      dom: 0,
      lun: 1,
      mar: 2,
      mer: 3,
      gio: 4,
      ven: 5,
      sab: 6,
    };

    const today = new Date();
    const todayIndex = today.getDay();
    const todayDate = today.getDate();
    let cutoff: Date = null;

    if (type === 'asporto' || type === 'takeaway') {
      const orderType = type[0].toUpperCase() + type.slice(1);
      cutoff = this.cutoff(
        today,
        setup['oreMinime' + orderType],
        setup['limiteOre' + orderType]
      );
    }

    const days = businessHours.map((g) => {
      // Calculate the date for the given day abbreviation.
      const dayIndex = indices[g.giorno.toLowerCase()];
      const dayDifference = (dayIndex - todayIndex + 7) % 7;
      const dayDate = new Date(today);
      dayDate.setDate(todayDate + dayDifference);

      // Build the hours for that day.
      const hours = this.buildHours(g.orari, dayDate);
      const filteredHours =
        cutoff && this.isSameDay(dayDate, today)
          ? hours.filter((time) => time > cutoff).filter((time) => time > today)
          : hours;

      return {
        id: g.id,
        giorno: dayDate,
        orari: filteredHours,
      };
    });

    return days
      .sort((a, b) => a.giorno.getTime() - b.giorno.getTime())
      .filter((day) => day.orari.length > 0);
  }

  private isSameDay(d1: Date, d2: Date): boolean {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  private cutoff(
    current: Date,
    minHoursBeforeOrder: number,
    maxTimeBeforeOrder: string
  ): Date {
    const minTimeBeforeOrder =
      minHoursBeforeOrder === 0 ? 20 / 60 : minHoursBeforeOrder;

    const date = new Date(
      current.getTime() + minTimeBeforeOrder * 60 * 60 * 1000
    );
    const [hours, minute, second] = maxTimeBeforeOrder.split(':').map(Number);
    const limitTime = new Date(current);
    limitTime.setHours(hours, minute, second, 0);
    if (date >= limitTime) {
      return limitTime;
    }
    return date;
  }
}
